import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, User, Phone, Mail, MapPin, ArrowUpLeft, Send, Briefcase, ChevronUp } from 'lucide-react';

const SOCIAL_PATHS = {
  x: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  instagram: 'M12 2.2c3.2 0 3.6 0 4.8.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 3.2-1.7 4.8-4.9 4.9-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-3.3-.1-4.8-1.7-4.9-4.9C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8C2.4 3.9 3.9 2.4 7.2 2.3 8.4 2.2 8.8 2.2 12 2.2zm0 4.8a5 5 0 100 10 5 5 0 000-10zm0 8.2a3.2 3.2 0 110-6.4 3.2 3.2 0 010 6.4zm5.2-9.6a1.2 1.2 0 100 2.4 1.2 1.2 0 000-2.4z',
  youtube: 'M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2 31 31 0 000 12a31 31 0 00.5 5.8 3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1A31 31 0 0024 12a31 31 0 00-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z',
  facebook: 'M24 12a12 12 0 10-13.9 11.9v-8.4h-3V12h3V9.4c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.2 2.7.2v3h-1.5c-1.5 0-2 .9-2 1.9V12h3.4l-.5 3.5h-2.9v8.4A12 12 0 0024 12z',
  tiktok: 'M19.6 6.7a4.8 4.8 0 01-3.8-4.2V2h-3.4v13.7a2.9 2.9 0 11-2-2.7V9.5a6.3 6.3 0 105.4 6.2V8.7a8.2 8.2 0 004.8 1.5V6.8z',
  snapchat: 'M12.2 1c2.4 0 4.6 1.3 5.6 3.5.5 1 .4 2.8.3 4.2l.1.1c.4.2 1.2-.2 1.6-.1.5.1.9.5.8 1-.1.7-1.3 1-2 1.3-.3.1-.5.4-.4.7.7 1.6 2.1 3 3.8 3.4.4.1.6.5.4.9-.5.9-2.1 1-2.6 1.4-.2.4-.1 1-.6 1.2-.7.2-1.6-.3-2.7 0-1.3.3-2.2 2.1-4.4 2.1s-3-1.8-4.4-2.1c-1.1-.3-2 .2-2.7 0-.5-.2-.4-.8-.6-1.2-.5-.4-2.1-.5-2.6-1.4-.2-.4 0-.8.4-.9 1.7-.4 3.1-1.8 3.8-3.4.1-.3-.1-.6-.4-.7-.7-.3-1.9-.6-2-1.3 0-.5.4-.9.9-1 .4-.1 1.1.3 1.5.1l.1-.1c-.1-1.4-.2-3.2.3-4.2C7.6 2.3 9.8 1 12.2 1z',
};

export const SocialLinks = ({ API, contactInfo, className = '', size = 'w-10 h-10' }) => {
  const sm = contactInfo?.social_media || {};
  const icons = contactInfo?.social_icons || {};
  const list = Object.keys(SOCIAL_PATHS).map((k) => ({ k, url: sm[`${k}_url`], file: icons[`${k}_icon_file`] })).filter((s) => s.url);
  if (!list.length) return null;
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {list.map((s) => (
        <a key={s.k} data-testid={`social-link-${s.k}`} href={s.url} target="_blank" rel="noopener noreferrer" className={`${size} rounded-full bg-white/10 hover:bg-orange-500 text-white flex items-center justify-center transition-colors`}>
          {s.file ? <img src={`${API}/files/${s.file}`} alt={s.k} className="w-1/2 h-1/2 object-contain" /> : <svg viewBox="0 0 24 24" className="w-1/2 h-1/2" fill="currentColor"><path d={SOCIAL_PATHS[s.k]} /></svg>}
        </a>
      ))}
    </div>
  );
};

const NAV = [
  { label: 'الرئيسية', href: '#home' },
  { label: 'الأخبار', href: '#news-magazine' },
  { label: 'المباريات', href: '#matches' },
  { label: 'الإنجازات', href: '#achievements' },
  { label: 'الاستراتيجية', href: '#strategy' },
  { label: 'المتجر', href: '/shop' },
  { label: 'مجلس الإدارة', action: 'board' },
];

const Logo = ({ logoUrl, clubName, subtitle, dark }) => (
  <a href="#home" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
    {logoUrl ? <img src={logoUrl} alt="" className="w-12 h-12 lg:w-14 lg:h-14 object-contain drop-shadow" /> : <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center font-black text-xl">أ</div>}
    <div className="leading-tight">
      <div className={`text-lg lg:text-xl font-black ${dark ? 'text-white' : 'text-slate-900'}`}>{clubName}</div>
      <div className="text-xs font-semibold text-orange-500">{subtitle}</div>
    </div>
  </a>
);

export const SiteHeader = ({ API, logoUrl, clubName, subtitle, showHeader, showLogin, contactInfo, onBoard, onContact }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; }, [open]);

  const go = (item) => {
    setOpen(false);
    if (item.action === 'board') onBoard();
  };
  const NavItem = ({ item, mobile }) => {
    const cls = mobile
      ? 'block w-full text-right px-4 py-3.5 rounded-xl text-lg font-bold text-white hover:bg-white/10 transition-colors'
      : 'relative px-3 xl:px-4 py-2 text-[15px] font-bold text-slate-700 hover:text-orange-600 transition-colors after:absolute after:bottom-0 after:right-3 after:left-3 after:h-[2px] after:bg-orange-500 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-right';
    const tid = `nav-${mobile ? 'm-' : ''}${item.href?.replace(/[#/]/g, '') || item.action}`;
    return item.action
      ? <button data-testid={tid} onClick={() => go(item)} className={cls}>{item.label}</button>
      : <a data-testid={tid} href={item.href} onClick={() => setOpen(false)} className={cls}>{item.label}</a>;
  };

  return (
    <>
    <header data-testid="site-header" className={`sticky top-0 z-50 transition-transform duration-300 ${showHeader || open ? 'translate-y-0' : '-translate-y-full'}`}>
      <div className={`transition-[background-color,box-shadow] duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(11,18,32,0.25)]' : 'bg-white/75 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4" dir="rtl">
          <Logo logoUrl={logoUrl} clubName={clubName} subtitle={subtitle} />
          <nav className="hidden lg:flex items-center">{NAV.map((n) => <NavItem key={n.label} item={n} />)}</nav>
          <div className="flex items-center gap-2">
            <button data-testid="header-contact-btn" onClick={onContact} className="hidden md:inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors shadow-lg shadow-orange-500/25">
              تواصل معنا <ArrowUpLeft className="w-4 h-4" />
            </button>
            {showLogin && (
              <Link to="/login" data-testid="header-login-btn" title="تسجيل الدخول" className="w-11 h-11 rounded-full border border-slate-200 bg-white hover:border-orange-400 hover:text-orange-600 text-slate-700 flex items-center justify-center transition-colors">
                <User className="w-5 h-5" />
              </Link>
            )}
            <button data-testid="header-menu-btn" onClick={() => setOpen(true)} className="lg:hidden w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center"><Menu className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="h-[3px] bg-gradient-to-l from-orange-500 via-amber-400 to-orange-600" />
      </div>
    </header>

      <div className={`lg:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} dir="rtl">
        <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
        <div data-testid="mobile-menu" className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-[#0B1220] p-6 flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between mb-8">
            <Logo logoUrl={logoUrl} clubName={clubName} subtitle={subtitle} dark />
            <button data-testid="mobile-menu-close-btn" onClick={() => setOpen(false)} className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
          </div>
          <nav className="flex-1 space-y-1 overflow-y-auto">{NAV.map((n) => <NavItem key={n.label} item={n} mobile />)}</nav>
          <button data-testid="mobile-contact-btn" onClick={() => { setOpen(false); onContact(); }} className="w-full bg-orange-500 text-white font-bold py-3.5 rounded-xl mb-4">تواصل معنا</button>
          <SocialLinks API={API} contactInfo={contactInfo} className="justify-center" />
        </div>
      </div>
    </>
  );
};

const FooterCol = ({ title, children }) => (
  <div>
    <h4 className="text-white font-bold mb-5 flex items-center gap-2"><span className="w-1.5 h-5 rounded-full bg-orange-500" />{title}</h4>
    {children}
  </div>
);

const FLink = ({ href, onClick, children, testid }) => {
  const cls = 'text-slate-400 hover:text-orange-400 hover:-translate-x-1 inline-block transition-[color,transform] text-sm';
  return <li>{onClick ? <button data-testid={testid} onClick={onClick} className={cls}>{children}</button> : <a data-testid={testid} href={href} className={cls}>{children}</a>}</li>;
};

export const SiteFooter = ({ API, logoUrl, clubName, description, established, location, contactInfo, onContact, onBoard, onLegal }) => (
  <footer data-testid="site-footer" className="relative bg-[#070C16] text-white overflow-hidden" dir="rtl">
    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'repeating-linear-gradient(135deg, #F97316 0 1px, transparent 1px 16px)' }} />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
      <div className="-mb-px pt-14">
        <div className="rounded-3xl bg-gradient-to-l from-orange-600 to-orange-500 p-8 lg:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-2xl shadow-orange-900/30">
          <div>
            <h3 className="text-2xl lg:text-3xl font-black">كن جزءاً من عائلة الأمجاد</h3>
            <p className="text-orange-100 mt-2 text-sm lg:text-base">تواصل معنا للرعاية والشراكات والانضمام لأكاديمية النادي</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <button data-testid="footer-cta-contact-btn" onClick={onContact} className="inline-flex items-center gap-2 bg-white text-orange-600 font-bold px-6 py-3 rounded-full hover:bg-orange-50 transition-colors whitespace-nowrap"><Send className="w-4 h-4" /> أرسل رسالة</button>
            <a data-testid="footer-cta-careers-btn" href="/careers" className="inline-flex items-center gap-2 border border-white/40 hover:bg-white/10 font-bold px-6 py-3 rounded-full transition-colors whitespace-nowrap"><Briefcase className="w-4 h-4" /> الوظائف</a>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-10 py-14">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            {logoUrl && <img src={logoUrl} alt="" className="w-16 h-16 object-contain" />}
            <div>
              <div className="text-2xl font-black">{clubName}</div>
              {established && <div className="text-xs text-orange-400 font-semibold">تأسس عام {established}{location ? ` • ${location}` : ''}</div>}
            </div>
          </div>
          {description && <p className="text-slate-400 text-sm leading-7 max-w-md mb-6">{description}</p>}
          <SocialLinks API={API} contactInfo={contactInfo} />
        </div>
        <div className="lg:col-span-2">
          <FooterCol title="روابط سريعة">
            <ul className="space-y-3">
              <FLink href="#news-magazine" testid="footer-link-news">الأخبار</FLink>
              <FLink href="#matches" testid="footer-link-matches">المباريات</FLink>
              <FLink href="/players" testid="footer-link-players">اللاعبون</FLink>
              <FLink href="#achievements" testid="footer-link-achievements">الإنجازات</FLink>
            </ul>
          </FooterCol>
        </div>
        <div className="lg:col-span-2">
          <FooterCol title="النادي">
            <ul className="space-y-3">
              <FLink href="/strategy" testid="footer-link-strategy">الاستراتيجية</FLink>
              <FLink onClick={onBoard} testid="footer-link-board">مجلس الإدارة</FLink>
              <FLink href="/shop" testid="footer-link-shop">المتجر</FLink>
              <FLink href="/careers" testid="footer-link-careers">الوظائف</FLink>
            </ul>
          </FooterCol>
        </div>
        <div className="lg:col-span-3">
          <FooterCol title="تواصل معنا">
            <ul className="space-y-4 text-sm">
              {!contactInfo?.phone && !contactInfo?.email && !contactInfo?.address && <li><button data-testid="footer-contact-fallback-btn" onClick={onContact} className="text-slate-400 hover:text-orange-400 transition-colors">راسلنا عبر نموذج التواصل</button></li>}
              {contactInfo?.phone && <li className="flex items-center gap-3 text-slate-300"><span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center"><Phone className="w-4 h-4 text-orange-400" /></span><span dir="ltr">{contactInfo.phone}</span></li>}
              {contactInfo?.email && <li className="flex items-center gap-3 text-slate-300"><span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center"><Mail className="w-4 h-4 text-orange-400" /></span><span className="break-all">{contactInfo.email}</span></li>}
              {contactInfo?.address && <li className="flex items-center gap-3 text-slate-300"><span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0"><MapPin className="w-4 h-4 text-orange-400" /></span>{contactInfo.address}</li>}
            </ul>
          </FooterCol>
        </div>
      </div>

      <div className="border-t border-white/10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
        <span>© {new Date().getFullYear()} {clubName}. جميع الحقوق محفوظة.</span>
        <div className="flex items-center gap-5">
          <button data-testid="footer-privacy-btn" onClick={() => onLegal('privacy')} className="hover:text-orange-400 transition-colors">سياسة الخصوصية</button>
          <button data-testid="footer-terms-btn" onClick={() => onLegal('terms')} className="hover:text-orange-400 transition-colors">الشروط والأحكام</button>
          <button data-testid="footer-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="w-9 h-9 rounded-full bg-white/5 hover:bg-orange-500 hover:text-white flex items-center justify-center transition-colors"><ChevronUp className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  </footer>
);
